import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { Moon, MoreHorizontal, Sun } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { NAVIGATION_ITEMS } from './constants';
import NavigationItem from './NavigationItem';

import styles from './Layout.module.css';

import { track } from '@/shared/analytics/track';
import SidebarBrandSwitcher from '@/shared/components/SidebarBrandSwitcher';
import SidebarOrgSwitcher from '@/shared/components/SidebarOrgSwitcher';
import { useAuth } from '@/shared/hooks/useAuth';
import { useFeatures } from '@/shared/hooks/useFeatures';
import { useTheme } from '@/shared/hooks/useTheme';
import {
  isPageEnabledForBrand,
  NAV_GROUPS
} from '@/shared/routes/pageRegistry';
import { APP_ROUTES, PUBLIC_ROUTES } from '@/shared/routes/paths';
import { useBrandStore } from '@/shared/stores/useBrandStore';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { logout, user } = useAuth();
  const { isAnyFeatureEnabled, isFeatureEnabled } = useFeatures();
  // Tax info and Contract are v3-only Organization tabs.
  const roadmapV3 = isFeatureEnabled('PROGRAMS_ROADMAP_V3');
  const brandId = useBrandStore((state) => state.brandId);
  const setBrandId = useBrandStore((state) => state.setBrandId);

  const visibleNavItems = useMemo(() => {
    // Org flag AND the selected brand's own flag: `user.features` ORs across
    // the org's brands, so a page enabled for one brand used to stay in the
    // rail while another was selected, then 403 on click.
    const selectedBrand = user?.brands.find((b) => b.id === brandId);

    return NAVIGATION_ITEMS.filter(
      (item) =>
        isAnyFeatureEnabled(item.accessFeatureKeys ?? item.featureKey) &&
        isPageEnabledForBrand(item, selectedBrand)
    );
  }, [user?.brands, brandId, isAnyFeatureEnabled]);

  useEffect(() => {
    // Only process brands after user data is loaded
    if (!user) {
      return;
    }

    const { brands } = user;

    if (brands.length === 0) {
      // Zero brands is a SUPPORTED state, not an error: every self-serve
      // organization starts with none, and the three pages it can reach
      // (ROI Calculator, Gift Cards, Service Offerings) reference no brandId.
      // Logging this as an error fired on every login and misled support triage.
      // The early return still matters — it's what prevents a brands[0] TypeError.
      console.debug('No brands for organization — skipping brand auto-select');
      return;
    }

    // Auto-select first brand if none selected
    const currentBrandId = useBrandStore.getState().brandId;
    if (!currentBrandId) {
      setBrandId(brands[0].id);
      track('brand_auto_selected', {
        to_brand_id: brands[0].id,
        to_brand_name: brands[0].name,
        reason: 'first_brand'
      });
    }
  }, [user, setBrandId]);

  // The rail is permanently icon-only; there is no expand control. This
  // supersedes S3's "labelled rail, collapsible to icons" recommendation —
  // recorded in specs/design/reviews/2026-08-portal-ux-audit.md. Every nav item
  // therefore carries a tooltip, which is the only thing naming it.
  const sidebarClasses = classNames(styles.sidebar, styles.collapsed);
  const mainShellClasses = classNames(
    styles.mainShell,
    styles.mainShellCollapsed
  );

  const mobileNavigationItems = [...visibleNavItems];

  const visibleMobileItems = mobileNavigationItems.slice(0, 3);
  const overflowMobileItems = mobileNavigationItems.slice(3);

  const renderNavigationItems = () => {
    if (visibleNavItems.length === 0) {
      return;
    }
    // Grouped so ten near-identical icons stop competing as one flat list.
    return NAV_GROUPS.map((group) => {
      const items = visibleNavItems.filter((item) => item.group === group);
      if (items.length === 0) return null;
      return (
        <li
          aria-label={group}
          className={styles.navGroup}
          key={group}
          role="group"
        >
          <ul className={styles.navGroupList}>
            {items.map((item) => (
              <NavigationItem item={item} key={item.path} />
            ))}
          </ul>
        </li>
      );
    });
  };

  const renderMobileNavigationItems = () => {
    if (visibleMobileItems.length === 0) {
      return;
    }
    return visibleMobileItems.map((item) => (
      <NavigationItem item={item} key={item.path} showTooltip={false} />
    ));
  };

  const renderOverflowMenuItem = (item: (typeof mobileNavigationItems)[0]) => {
    const IconComponent = item.icon;
    const isActive = location.pathname === item.path;

    return (
      <button
        key={item.path}
        type="button"
        className={classNames(styles.overflowMenuItem, {
          [styles.active]: isActive
        })}
        onClick={() => {
          navigate(item.path);
          setIsMobileMenuOpen(false);
        }}
      >
        <span className={styles.overflowMenuIcon}>
          <IconComponent size={20} />
        </span>
        <span className={styles.overflowMenuText}>{item.title}</span>
      </button>
    );
  };

  const userInitials = user
    ? `${user.firstName?.[0] ?? user.email[0]}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : 'U';

  const { isDark, toggleTheme } = useTheme();
  const isUserSectionActive =
    location.pathname === APP_ROUTES.PROFILE ||
    location.pathname === APP_ROUTES.ORGANIZATION;

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className={styles.layout}>
      <aside className={sidebarClasses}>
        <div className={styles.sidebarHeader}>
          <div className={styles.headerContent}>
            <div className={styles.collapsedEmblem}>
              <img
                alt="ThredUp"
                className={styles.emblemLogo}
                src="/thredup-emblem.svg"
              />
            </div>
          </div>
        </div>

        <nav className={styles.navigation}>
          <ul className={`${styles.navList} ${styles.desktopNav}`}>
            {renderNavigationItems()}
          </ul>
          <div className={`${styles.navList} ${styles.mobileNav}`}>
            {renderMobileNavigationItems()}
            {overflowMobileItems.length > 0 && (
              <div className={styles.overflowMenuContainer}>
                <button
                  type="button"
                  className={styles.overflowMenuButton}
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  aria-label="More navigation items"
                >
                  <span className={styles.navIcon}>
                    <MoreHorizontal size={20} />
                  </span>
                  <span className={styles.navText}>More</span>
                </button>
                {isMobileMenuOpen && (
                  <>
                    <div
                      className={styles.overflowMenuBackdrop}
                      onClick={() => setIsMobileMenuOpen(false)}
                    />
                    <div className={styles.overflowMenu}>
                      {overflowMobileItems.map(renderOverflowMenuItem)}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </nav>

        <div className={styles.sidebarFooter}>
          {/* Dark theme toggle — hidden for now, revisit later
          <button
            type="button"
            className={styles.themeToggle}
            onClick={toggleTheme}
            aria-label={
              isDark ? 'Switch to light theme' : 'Switch to dark theme'
            }
          >
            {isDark ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          */}

          {/* Scope block: organization -> brand -> you, read top to bottom.
              The brand switcher used to be a lone dropdown in a top bar, which
              made the same value editable in two places on the Cashout page. */}
          <SidebarOrgSwitcher />
          <SidebarBrandSwitcher />
          <div className={styles.divider} />

          <div className={styles.userSection} ref={userMenuRef}>
            <button
              type="button"
              className={classNames(styles.userMenuTrigger, {
                [styles.active]: isUserMenuOpen || isUserSectionActive
              })}
              onClick={() => setIsUserMenuOpen((v) => !v)}
              aria-label="User menu"
            >
              <div className={styles.avatarPlaceholder}>{userInitials}</div>
              {/* S3: "The two stacked circles at the bottom (TH, AZ) are org
                  and user with nothing explaining either." When there is room,
                  say who and what. */}
            </button>

            {isUserMenuOpen && (
              <div className={styles.userMenu} role="menu">
                <div className={styles.userMenuHeader}>
                  <span className={styles.userMenuName}>
                    {[user?.firstName, user?.lastName]
                      .filter(Boolean)
                      .join(' ') || user?.email}
                  </span>
                  <span className={styles.userMenuEmail}>{user?.email}</span>
                  {user?.role && (
                    <span className={styles.userMenuRole}>{user.role}</span>
                  )}
                </div>
                <div className={styles.userMenuDivider} />
                <button
                  type="button"
                  className={styles.userMenuItem}
                  onClick={() => {
                    navigate(APP_ROUTES.PROFILE);
                    setIsUserMenuOpen(false);
                  }}
                >
                  User Settings
                </button>
                <button
                  type="button"
                  className={styles.userMenuItem}
                  onClick={() => {
                    navigate(APP_ROUTES.ORGANIZATION);
                    setIsUserMenuOpen(false);
                  }}
                >
                  My Organization
                </button>
                {roadmapV3 && (
                  <>
                    <button
                      type="button"
                      className={styles.userMenuItem}
                      onClick={() => {
                        navigate(`${APP_ROUTES.ORGANIZATION}?tab=tax-info`);
                        setIsUserMenuOpen(false);
                      }}
                    >
                      Tax info
                    </button>
                    <button
                      type="button"
                      className={styles.userMenuItem}
                      onClick={() => {
                        navigate(`${APP_ROUTES.ORGANIZATION}?tab=contract`);
                        setIsUserMenuOpen(false);
                      }}
                    >
                      Contract
                    </button>
                  </>
                )}
                <div className={styles.userMenuDivider} />
                <button
                  type="button"
                  className={`${styles.userMenuItem} ${styles.userMenuSignOut}`}
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    // Navigate off the authenticated tree before `user` becomes
                    // null, otherwise the auth gate re-renders first and bounces
                    // straight back into SSO.
                    navigate(PUBLIC_ROUTES.LANDING, { replace: true });
                    setBrandId(null);
                    queryClient.clear();
                    void logout();
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      <div className={mainShellClasses}>
        {/* Pages feed extra controls into the shared TopHeader via the
            header-controls slot rather than their own in-page bar. */}
        <main className={styles.mainContent}>
          <div className={styles.contentWrapper}>{children}</div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
